import { inject, Injector } from '@angular/core';
import { MarketplaceItemV2 } from './models/marketplace-item-v2.model';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { BalanceQuery } from '../../../account/balance/balance.query';
import { ProcessProviderId } from '../models/process-provider-id';
import { config } from '../../../shared/config';
import { EntityOptions, withEntityState } from '../../../shared/store/entity-state.feature';
import { REWARD_STATUS } from '../../shared/models/reward-status.enum';
import { DISABLED_STATUSES } from './models/marketplace-disabled-status.enum';

type MarketplaceState = {
  selectedItem: MarketplaceItemV2 | null;
};

const initialState: MarketplaceState = {
  selectedItem: null,
};

/**
 * Derives the effective display status of a marketplace item based on the user's current balance.
 *
 * For eCash items, the combined purse balance is used (converted from cents).
 * For points-based items, the virtual loyalty purse balance is used.
 * If the item's cost exceeds the available balance, the status is overridden to `NotEnoughPoints`.
 */
export function resolveMarketplaceStatus(item: MarketplaceItemV2, injector?: Injector): REWARD_STATUS {
  if (!injector) {
    return item.Status;
  }
  const balanceQuery = injector.get(BalanceQuery);
  const isEcash = item.EcashValue > 0;
  const prop: keyof MarketplaceItemV2 = isEcash ? 'EcashValue' : 'PointValue';
  const conf = isEcash ? config.purseConfig.combined : config.purseConfig.virtualLoyalty;
  const balance = isEcash ? balanceQuery.getPurse(conf) / 100 : balanceQuery.getPurse(conf) || 0;

  const status = item[prop] > balance ? REWARD_STATUS.NotEnoughPoints : item.Status;

  if (!DISABLED_STATUSES.has(status)) {
    if (item.LimitType > 0) {
      return REWARD_STATUS.LimitedItem;
    } else if (item.StockCount > 0 && item.StockCount <= 10) {
      return REWARD_STATUS.LimitedStock;
    }
  }
  return status;
}

/**
 * Signal-based NgRx store for marketplace items (v2).
 *
 * Manages the entity collection of `MarketplaceItemV2` records. Computed signals automatically resolve each item's effective
 * status against the user's current balance on every read.
 */
export const MarketplaceItemStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withEntityState<MarketplaceItemV2, 'Id'>('Id', {
    sortBy: { key: 'Id', order: 'desc' },
    cacheTtl: 60000,
    updaterFn: (item, injector) => ({ Status: resolveMarketplaceStatus(item, injector) }),
  }),
  withMethods((store, injector = inject(Injector)) => ({
    /** Returns a signal resolving to the full list of marketplace items, with resolved status. */
    getMarketplaceItems(options: EntityOptions = {}) {
      return store.getAll(options);
    },
    /** Returns a signal resolving to all marketplace items whose `ProcessProviderId` matches one of the provided types. */
    getItemsOfType(types: ProcessProviderId[], options: EntityOptions = {}) {
      return this.getMarketplaceItems({ filterBy: (item) => types.includes(item.ProcessProviderId), sortBy: options.sortBy });
    },
    /** Create separate store entry for selected item because the list and detail status is inconsistent */
    setSelectedItem(item: MarketplaceItemV2 | null) {
      if (!item) {
        patchState(store, { selectedItem: null });
        return;
      }
      const selectedItem = { ...item, Status: resolveMarketplaceStatus(item, injector) };
      patchState(store, { selectedItem });
    },
  }))
);
